/* 7AYAA — cart.

   Every mutation goes to Shopify and comes back as re-rendered Liquid via the
   Section Rendering API. Nothing here recalculates a total, a line price or a
   discount: those are Shopify's answers, and duplicating that arithmetic in
   JavaScript is how a cart starts disagreeing with checkout.

   All listeners are delegated, because the drawer's markup is replaced
   wholesale on every change. */
(function () {
  'use strict';

  var SECTIONS = 'cart-drawer,cart-icon-bubble';
  var pending = null;

  function drawerEl() {
    return document.querySelector('[data-cart-drawer]');
  }

  /* ------------------------------------------------------------ OPEN */
  function open() {
    var drawer = drawerEl();
    if (!drawer || !window.s7Drawer) return;
    if (drawer.classList.contains('is-open')) return; // idempotent
    drawer.classList.add('is-open');
    window.s7Drawer.open(drawer, document.activeElement);
  }

  function close() {
    if (window.s7Drawer) window.s7Drawer.close();
  }

  /* --------------------------------------------------------- RENDER */
  /* The HTML here is first-party: Shopify's Section Rendering API returning
     our own Liquid sections from the same origin. Customer-supplied values
     inside it (line item properties, the cart note) are escaped in Liquid at
     the point of output, not trusted to be safe here. */
  function applySections(sections) {
    if (!sections) return;

    if (sections['cart-drawer']) {
      var parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      var fresh = parsed.querySelector('[data-cart-drawer]');
      var live = drawerEl();
      /* Replace the contents, not the element — the focus trap and the
         open/closed class live on the wrapper. */
      if (fresh && live) live.innerHTML = fresh.innerHTML;
    }

    if (sections['cart-icon-bubble']) {
      var parsedBubble = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      var freshLink = parsedBubble.querySelector('[data-cart-link]');
      var liveLink = document.querySelector('[data-cart-link]');

      if (freshLink && liveLink) {
        /* Read the count before and after so the pulse only fires on an actual
           change. Re-rendering the bubble for an unrelated reason should not
           animate it — that is the notification spam the brief rules out. */
        var before = countOf(liveLink);
        liveLink.replaceWith(freshLink);
        if (countOf(freshLink) !== before) bumpBadge(freshLink);
      }
    }
  }

  function countOf(link) {
    var el = link.querySelector('[data-cart-count], .s7-cart-count, .s7-cart-link__badge');
    return el ? el.textContent.trim() : null;
  }

  /* One pulse, then the class comes off so the next add can re-fire it. */
  function bumpBadge(link) {
    var badge = link.querySelector('.s7-cart-link__badge') || link.querySelector('.s7-cart-count');
    if (!badge) return;
    badge.classList.remove('is-bumped');
    /* Reading offsetWidth forces the style flush that lets the class re-apply
       as a new animation rather than being coalesced into a no-op. */
    void badge.offsetWidth;
    badge.classList.add('is-bumped');
    badge.addEventListener('animationend', function () {
      badge.classList.remove('is-bumped');
    }, { once: true });
  }

  /* NO Accept header here, deliberately.

     GET /cart?sections=… and GET /cart are the same route, and Shopify picks
     the representation from Accept. With `Accept: application/json` it answers
     with the CART OBJECT — token, note, items, item_count — and ignores the
     sections parameter entirely. The response is still 200 and still JSON, so
     nothing throws: applySections just looks for a 'cart-drawer' key that is
     not there and returns having done nothing.

     That was the "add to cart doesn't work" bug. The item really was added and
     the drawer really did open; it just kept rendering the empty-cart markup
     from page load, and the bag count stayed at 0.

     Left alone, the same route returns { "cart-drawer": "…", "cart-icon-bubble":
     "…" }, which is what this needs. */
  function refresh() {
    return fetch(window.routes.cart_url + '?sections=' + encodeURIComponent(SECTIONS))
      .then(function (r) { return r.json(); })
      .then(applySections)
      .catch(function () { /* leave the current render in place */ });
  }

  /* --------------------------------------------------------- CHANGE */
  function change(line, quantity, sourceInput) {
    if (pending) return pending;

    setBusy(true);
    clearError();

    pending = fetch(window.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        line: line,
        quantity: quantity,
        sections: SECTIONS,
        sections_url: window.location.pathname
      })
    })
      .then(function (res) {
        return res.json().then(function (data) {
          return { ok: res.ok, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          /* 422 — most often the requested quantity exceeds what is in stock.
             Shopify's description names the real limit, so show it verbatim
             rather than inventing a number. */
          showError(result.data.description || result.data.message);
          if (sourceInput) sourceInput.value = sourceInput.defaultValue;
          return;
        }
        applySections(result.data.sections);
        document.dispatchEvent(new CustomEvent('s7:cart:updated', { detail: { cart: result.data } }));
      })
      .catch(function () {
        showError(window.s7Strings && window.s7Strings.cartError);
      })
      .then(function () {
        setBusy(false);
        pending = null;
      });

    return pending;
  }

  function setBusy(state) {
    var drawer = drawerEl();
    if (drawer) drawer.setAttribute('aria-busy', String(state));
  }

  function showError(message) {
    var host = document.querySelector('[data-cart-error]');
    if (!host) {
      var body = document.querySelector('.s7-cart-drawer__body, .s7-cart-page__items');
      if (!body) return;
      host = document.createElement('p');
      host.className = 's7-form-status s7-form-status--error';
      host.setAttribute('role', 'alert');
      host.setAttribute('data-cart-error', '');
      body.prepend(host);
    }
    host.textContent = message || 'Something went wrong. Please try again.';
    host.hidden = false;
  }

  function clearError() {
    var host = document.querySelector('[data-cart-error]');
    if (host) host.hidden = true;
  }

  /* ----------------------------------------------------- DELEGATION */
  function lineOf(el) {
    return parseInt(el.dataset.line, 10);
  }

  document.addEventListener('click', function (ev) {
    var openBtn = ev.target.closest('[data-cart-open]');
    if (openBtn && drawerEl()) {
      ev.preventDefault();
      open();
      return;
    }

    var down = ev.target.closest('[data-qty-down]');
    if (down) {
      var inputDown = document.querySelector('[data-qty-input][data-line="' + down.dataset.line + '"]');
      var nextDown = Math.max(0, (parseInt(inputDown.value, 10) || 0) - 1);
      change(lineOf(down), nextDown, inputDown);
      return;
    }

    var up = ev.target.closest('[data-qty-up]');
    if (up) {
      var inputUp = document.querySelector('[data-qty-input][data-line="' + up.dataset.line + '"]');
      change(lineOf(up), (parseInt(inputUp.value, 10) || 0) + 1, inputUp);
      return;
    }

    var remove = ev.target.closest('[data-qty-remove]');
    if (remove) {
      change(lineOf(remove), 0);
    }
  });

  /* Typed quantities commit on change, not on every keystroke. */
  document.addEventListener('change', function (ev) {
    var input = ev.target.closest('[data-qty-input]');
    if (!input) return;
    var value = parseInt(input.value, 10);
    if (isNaN(value) || value < 0) {
      input.value = input.defaultValue;
      return;
    }
    change(lineOf(input), value, input);
  });

  /* A successful add from the product form: open immediately so the
     interaction feels instant, then let the refresh fill in the real state. */
  document.addEventListener('s7:cart:added', function (ev) {
    open();
    /* /cart/add.js already returns the re-rendered sections when asked, so the
       common path needs no second request. refresh() stays as the fallback for
       anything that fires this event without them. */
    var sections = ev.detail && ev.detail.sections;
    if (sections && sections['cart-drawer']) applySections(sections);
    else refresh();
  });

  window.s7Cart = { open: open, close: close, refresh: refresh, change: change };
})();
